// /opt/ptg01-v1/front-extranet/lib/status.ts
// Palette d'états centralisée — résout les divergences couleur/label entre pages.
// Chaque page (CRA, mission, salaire, mouvement bancaire…) mappe son enum métier
// vers une "intention visuelle" commune. Cela garantit que :
//   - "amber" = en attente partout (pas en attente sur CRA, encaissé sur Finance…)
//   - "green" = terminal/validé partout
//   - "rose"  = action requise partout
// Audit UX 2026-04-27 : 3 STATUS_META dupliqués → 1 source de vérité.

export type StatusIntent =
    | 'neutral'    // brouillon, en édition (zéro charge cognitive)
    | 'pending'    // en attente d'action ou de validation côté autre acteur
    | 'progress'   // en cours d'exécution (paiement émis, pas encore reçu)
    | 'success'    // validé, payé, reçu, terminal positif
    | 'error'      // rejeté, action requise
    | 'info';      // info neutre type "attribué" (titres-resto)

export interface StatusStyle {
    bg: string;
    text: string;
    border: string;
    /** Pastille colorée plus foncée pour les badges */
    dot: string;
}

/**
 * Palette commune. Toutes les couleurs Tailwind, cohérentes avec la charte Nexvia.
 * Le bleu primaire (#000091) est réservé à `info` ; il ne doit JAMAIS coder un état
 * fonctionnel pour éviter la collision avec les boutons d'action et les liens.
 */
export const STATUS_STYLES: Record<StatusIntent, StatusStyle> = {
    neutral: {
        bg: 'bg-slate-50',
        text: 'text-slate-700',
        border: 'border-slate-200',
        dot: 'bg-slate-400',
    },
    pending: {
        bg: 'bg-amber-50',
        text: 'text-amber-700',
        border: 'border-amber-200',
        dot: 'bg-amber-500',
    },
    progress: {
        bg: 'bg-sky-50',
        text: 'text-sky-700',
        border: 'border-sky-200',
        dot: 'bg-sky-500',
    },
    success: {
        bg: 'bg-green-50',
        text: 'text-green-700',
        border: 'border-green-200',
        dot: 'bg-green-500',
    },
    error: {
        bg: 'bg-rose-50',
        text: 'text-rose-700',
        border: 'border-rose-200',
        dot: 'bg-rose-500',
    },
    info: {
        bg: 'bg-[#000091]/[0.05]',
        text: 'text-[#000091]',
        border: 'border-[#000091]/20',
        dot: 'bg-[#000091]',
    },
};

// ─────────────────────────────────────────────────────────────────────────────
// Mappings métier → intention visuelle. Chaque enum domain s'adresse à une seule
// intention, et la couleur découle de l'intention (jamais l'inverse).
// ─────────────────────────────────────────────────────────────────────────────

/** Statuts CRA / Notes de frais / Salaire — workflow de validation typique */
export type WorkflowStatus = 'DRAFT' | 'SUBMITTED' | 'VALIDATED' | 'REJECTED' | 'LOCKED';

export const WORKFLOW_INTENT: Record<WorkflowStatus, StatusIntent> = {
    DRAFT:     'neutral',
    SUBMITTED: 'pending',
    VALIDATED: 'success',
    REJECTED:  'error',
    LOCKED:    'success',
};

export const WORKFLOW_LABEL: Record<WorkflowStatus, string> = {
    DRAFT:     'Brouillon',
    SUBMITTED: 'Soumis',
    VALIDATED: 'Validé',
    REJECTED:  'À corriger',
    LOCKED:    'Verrouillé',
};

/** Statuts compte d'activité mensuel */
export type ActivityAccountStatus = 'DRAFT' | 'GENERATED' | 'LOCKED';

export const ACTIVITY_ACCOUNT_INTENT: Record<ActivityAccountStatus, StatusIntent> = {
    DRAFT:     'neutral',
    GENERATED: 'pending',
    LOCKED:    'success',
};

export const ACTIVITY_ACCOUNT_LABEL: Record<ActivityAccountStatus, string> = {
    DRAFT:     'Brouillon',
    GENERATED: 'À valider',
    LOCKED:    'Versé',
};

/** Mouvements bancaires (relevé) */
export type BankLineStatus = 'recu' | 'percu' | 'en_cours' | 'attribue' | 'rembourse';

export const BANK_LINE_INTENT: Record<BankLineStatus, StatusIntent> = {
    recu:      'success',
    percu:     'success',
    en_cours:  'pending',
    attribue:  'info',
    rembourse: 'success',
};

export const BANK_LINE_LABEL: Record<BankLineStatus, string> = {
    recu:      'Reçu',
    percu:     'Perçu',
    en_cours:  'En cours',
    attribue:  'Attribué',
    rembourse: 'Remboursé',
};

// ─────────────────────────────────────────────────────────────────────────────
// Helpers : style + label en 1 appel
// ─────────────────────────────────────────────────────────────────────────────

export function getWorkflowStyle(status: WorkflowStatus): StatusStyle {
    return STATUS_STYLES[WORKFLOW_INTENT[status]];
}
export function getActivityAccountStyle(status: ActivityAccountStatus): StatusStyle {
    return STATUS_STYLES[ACTIVITY_ACCOUNT_INTENT[status]];
}
export function getBankLineStyle(status: BankLineStatus): StatusStyle {
    return STATUS_STYLES[BANK_LINE_INTENT[status]];
}
